import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, ErrorContainer, Form, FormCheckbox, FormControl, FormField, FormInput, FormItem, FormLabel, FormMessage, FormMultipleInput, FormNumberInput, Heading, IconBin, IconChevronLeft, IconChevronRight, IconCreate, IconTick, IconUser, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Text } from "@sentricmusic/ui";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
// import { toast } from "sonner";
import { toast } from "sonner";
import { z } from "zod";

import { useGetMyWriters } from "@/api/queries";
import useSiteConfigAuth from "@/api/sanity/queries/getSiteConfigWithCode";
import { sanitizeInput } from "@/helpers/helpers";
import useAppStore from "@/store/appStateZustand";
import { useUIStateStore } from "@/store/uiStateStore";

import type { WriterForSelection } from "../../components";
import type { Writer } from "../types/writer";

import { CreateWriterModal, SelectExistingWritersModal } from "../../components";
import useMultiSongStore from "../store/multiSongStore";
import useSongWriterAssignmentStore from "../store/songWriterAssignmentStore";
import useWriterStore from "../store/writerStore";

// Zod schema for song form validation - function to create dynamic validation based on context
function createSongFormSchema(isTunecore: boolean, availableWriters: Writer[]) {
  return z.object({
    // Song details
    songTitle: z
      .string()
      .min(1, "Song title is required")
      .max(200, "Song title must be less than 200 characters")
      .refine((value) => {
        // Check if sanitized version is different from original
        const sanitized = sanitizeInput(value);
        const safeSanitized = sanitized || "";
        return safeSanitized === value;
      }, "Song title contains invalid characters"),

    durationMins: z
      .number()
      .min(0, "Duration minutes cannot be negative")
      .max(60, "Duration minutes must be less than 60"),

    durationSecs: z
      .number()
      .min(0, "Duration seconds cannot be negative")
      .max(59, "Duration seconds must be less than 60"),

    alternativeTitles: z
      .array(z.string())
      .refine((values) => {
        // Check if all values are valid - this ensures we can provide a proper error message
        return values.every((value) => {
          if (!value || value.trim() === "")
            return true; // Empty strings are allowed
          const sanitized = sanitizeInput(value);
          const safeSanitized = sanitized || "";
          return safeSanitized === value;
        });
      }, "One or more alternative titles contain invalid characters"),

    performingArtists: z
      .array(z.string().min(1, "Performing artist name is required").max(100, "Artist name must be less than 100 characters"))
      .min(1, "At least one performing artist is required")
      .refine((values) => {
        // Check if all values are valid - this ensures we can provide a proper error message
        return values.every((value) => {
          const sanitized = sanitizeInput(value);
          const safeSanitized = sanitized || "";
          return safeSanitized === value;
        });
      }, "One or more performing artist names contain invalid characters"),

    // Writer validation
    writers: z
      .array(z.object({
        writercode: z.string(),
        writername: z.string(),
        writershare: z.number().min(0).max(100),
        writerdesignationcode: z.number().min(1).max(3),
        righttocollect: z.boolean(),
      }))
      .min(1, "At least one writer is required")
      .refine((writers) => {
        const totalShares = writers.reduce((sum, writer) => sum + writer.writershare, 0);
        return Math.abs(totalShares - 100) < 0.01;
      }, "Total writer shares must equal 100%")
      .refine((writers) => {
        return writers.some(writer => writer.righttocollect === true);
      }, "At least one writer must have 'Right To Collect' enabled")
      .refine((writers) => {
        // Tunecore-specific validation: Writers with Right To Collect must have DOB and IPI
        if (!isTunecore)
          return true;

        const writersWithRTC = writers.filter(writer => writer.righttocollect === true);

        for (const writer of writersWithRTC) {
          const fullWriter = availableWriters.find(w => w.writercode === writer.writercode);
          if (!fullWriter)
            continue;

          const hasDateOfBirth = Boolean(fullWriter.dateofbirth != null && fullWriter.dateofbirth.trim() !== "");
          const hasCAEIPI = Boolean(fullWriter.caeipinumber != null && fullWriter.caeipinumber.trim() !== "");

          // Check if writer has IMRO registration pending (writerexternalregistrationstatus not null)
          const hasIMRORegistrationPending = Boolean(fullWriter.writerexternalregistrationstatus != null);

          // For writers with IMRO registration pending, only require Date of Birth (IPI/CAE not required)
          if (hasIMRORegistrationPending) {
            if (!hasDateOfBirth) {
              return false;
            }
          }
          else {
            // For other writers, require both Date of Birth and IPI/CAE
            if (!hasDateOfBirth || !hasCAEIPI) {
              return false;
            }
          }
        }

        return true;
      }, "For Tunecore submissions, writers with 'Right To Collect' must have Date of Birth. Writers without pending IMRO registration also require IPI/CAE number"),
  });
}

type SongFormData = z.infer<ReturnType<typeof createSongFormSchema>>;

// Types for writer splits with shares
export type WriterShareData = {
  writercode: string;
  writername: string;
  writershare: number;
  writerdesignationcode: number;
  righttocollect: boolean;
};

export type SongShareData = {
  songId: string;
  songTitle: string;
  durationMins: number;
  durationSecs: number;
  alternativeTitles: string[];
  performingArtists: string[];
  writers: WriterShareData[];
};

export default function DetailsAndSharesStep() {
  const { t } = useTranslation(["album", "writers", "common"]);
  const { tracks: selectedTracks } = useMultiSongStore();
  const { assignments } = useSongWriterAssignmentStore();
  const toggleWriterForSong = useSongWriterAssignmentStore(state => state.toggleWriterForSong);
  const { selectedWriters, availableWriters, setAvailableWriters, addSelectedWriter } = useWriterStore();
  const { songShares, setSongShares, currentSongIndex, setCurrentSongIndex, setTrackReviewAction } = useMultiSongStore();
  const isTunecore = useUIStateStore(state => state.isTunecore);
  const { data: siteConfigData } = useSiteConfigAuth();
  const selectedAccount = useAppStore(s => s.selectedAccount);

  // Modal states for adding writers
  const [isSelectModalOpen, setIsSelectModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isUnlockConfirmOpen, setIsUnlockConfirmOpen] = useState(false);

  // Fetch fresh writer data to ensure we have the latest information (including updated IPI numbers)
  const writersQuery = useGetMyWriters({ aac: selectedAccount as string });

  // Update availableWriters in store when fresh data is fetched
  useEffect(() => {
    if (writersQuery.data?.writers) {
      setAvailableWriters(writersQuery.data.writers);
    }
  }, [writersQuery.data, setAvailableWriters]);

  // Writer designation codes mapping
  const DESIGNATION_OPTIONS = [
    { value: 1, label: t("album:author") ?? "Author" },
    { value: 2, label: t("album:composer") ?? "Composer" },
    { value: 3, label: t("album:composer-author") ?? "Composer/Author" },
  ];

  // Current song being edited
  // Using currentSongIndex from store instead of local state

  // Form for current song
  const songFormSchema = React.useMemo(() => createSongFormSchema(isTunecore, availableWriters), [isTunecore, availableWriters]);
  const form = useForm<SongFormData>({
    resolver: zodResolver(songFormSchema),
    mode: "onChange",
    defaultValues: {
      songTitle: "",
      durationMins: 0,
      durationSecs: 0,
      alternativeTitles: [],
      performingArtists: [],
      writers: [],
    },
  });

  // Get all songs that have writers assigned OR are recordings/variants OR were unlocked
  // Recordings/variants need to appear so users can navigate to them and unlock if needed
  // Unlocked tracks (reviewAction === "dismiss") with no writers need to stay visible so user can add writers
  const songsWithWriters = React.useMemo(() => {
    return selectedTracks
      .filter(track => track.isSelected)
      .map((track) => {
        const songAssignments = assignments[track.id];
        const assignedWriters = selectedWriters
          .filter(writer => songAssignments?.[writer.writercode])
          .map((selectedWriter) => {
            const fullWriter = availableWriters.find(w => w.writercode === selectedWriter.writercode);
            return fullWriter || selectedWriter;
          });

        // Check if this is a recording or variant (should appear even without writers assigned)
        const isRecording = track.isSuspectedDuplicate === true && track.reviewAction !== "dismiss";
        const isVariant = typeof track.suspectedAlbumTrackId === "string" && track.suspectedAlbumTrackId.length > 0 && track.reviewAction !== "dismiss";
        const isRecordingOrVariant = isRecording || isVariant;

        // Check if this was a recording/variant that was unlocked (reviewAction === "dismiss")
        // These tracks need to stay visible so user can add writers
        const wasRecordingOrVariant = track.isSuspectedDuplicate === true || (typeof track.suspectedAlbumTrackId === "string" && track.suspectedAlbumTrackId.length > 0);
        const wasUnlocked = wasRecordingOrVariant && track.reviewAction === "dismiss";

        return {
          songId: track.id,
          songTitle: track.name,
          assignedWriters,
          isRecordingOrVariant,
          wasUnlocked,
        };
      })
      .filter(song => song.assignedWriters.length > 0 || song.isRecordingOrVariant || song.wasUnlocked);
  }, [selectedTracks, selectedWriters, availableWriters, assignments]);

  // Ensure currentSongIndex is within bounds
  React.useEffect(() => {
    if (songsWithWriters.length > 0 && (currentSongIndex >= songsWithWriters.length || currentSongIndex < 0)) {
      setCurrentSongIndex(0);
    }
  }, [songsWithWriters.length, currentSongIndex, setCurrentSongIndex]);

  // Initialize and sync song shares from writer assignments
  useEffect(() => {
    if (songsWithWriters.length === 0)
      return;

    const updatedShares: Record<string, SongShareData> = { ...songShares };
    let hasChanges = false;

    // Helper function to create writer display name
    const getWriterDisplayName = (writer: any): string => {
      const w = writer as { writername?: string; firstname?: string; lastname?: string };
      if (w.writername != null)
        return w.writername;
      if (w.firstname != null || w.lastname != null) {
        return `${w.firstname ?? ""} ${w.lastname ?? ""}`.trim();
      }
      return t("album:unknown-writer") ?? "Unknown Writer";
    };

    songsWithWriters.forEach((song) => {
      const existingData = updatedShares[song.songId];

      if (existingData == null) {
        // Initialize new song
        hasChanges = true;
        const track = selectedTracks.find(t => t.id === song.songId);

        const performingArtists = track?.artists?.map((a) => {
          const sanitized = sanitizeInput(a.name);
          return sanitized && sanitized.trim() ? sanitized : a.name;
        }).filter(name => name && name.trim()) || [];

        updatedShares[song.songId] = {
          songId: song.songId,
          songTitle: song.songTitle,
          durationMins: track ? Math.floor(track.duration_ms / 60000) : 0,
          durationSecs: track ? Math.floor((track.duration_ms % 60000) / 1000) : 0,
          alternativeTitles: [],
          performingArtists,
          writers: song.assignedWriters.map(writer => ({
            writercode: writer.writercode,
            writername: getWriterDisplayName(writer),
            writershare: 0,
            writerdesignationcode: 3,
            righttocollect: false,
          })),
        };
      }
      else {
        // Sync existing song's writers
        const currentWriterCodes = song.assignedWriters.map(w => w.writercode);
        const existingWriterCodes = existingData.writers.map(w => w.writercode);

        if (JSON.stringify(currentWriterCodes.sort()) !== JSON.stringify(existingWriterCodes.sort())) {
          hasChanges = true;

          updatedShares[song.songId] = {
            ...existingData,
            writers: [
              // Keep existing writers that are still assigned
              ...existingData.writers.filter(w => currentWriterCodes.includes(w.writercode)),
              // Add new writers
              ...song.assignedWriters
                .filter(w => !existingWriterCodes.includes(w.writercode))
                .map(writer => ({
                  writercode: writer.writercode,
                  writername: getWriterDisplayName(writer),
                  writershare: 0,
                  writerdesignationcode: 3,
                  righttocollect: false,
                })),
            ],
          };
        }
      }
    });

    if (hasChanges)
      setSongShares(updatedShares);
  }, [songsWithWriters, selectedTracks, songShares, setSongShares, t]);

  const currentSong = songsWithWriters[currentSongIndex];
  const currentSongData = currentSong != null ? songShares[currentSong.songId] : null;

  // Check if current song is a recording being added to an existing work (includes both account-level and intra-album matches)
  const currentTrack = currentSong != null ? selectedTracks.find(t => t.id === currentSong.songId) : null;
  const isAccountRecording = currentTrack != null && currentTrack.isSuspectedDuplicate === true && currentTrack.reviewAction !== "dismiss";
  const isVariant = currentTrack != null && typeof currentTrack.suspectedAlbumTrackId === "string" && currentTrack.suspectedAlbumTrackId.length > 0 && currentTrack.reviewAction !== "dismiss";
  const isRecording = isAccountRecording || isVariant;
  const isSharesLocked = false; // Shares are no longer locked for recordings

  // Helper function to check if song title has invalid characters
  const getTitleValidationInfo = (title: string) => {
    const sanitized = sanitizeInput(title);
    // Handle cases where sanitizeInput returns undefined, null, or empty string
    const safeSanitized = sanitized || "";
    const hasInvalidChars = safeSanitized !== title;

    return {
      hasInvalidChars,
      suggestedTitle: safeSanitized || (t("album:no-valid-characters-found") ?? "(no valid characters found)"),
    };
  };

  // Helper function to validate alternative titles
  const validateAlternativeTitle = (title: string) => {
    if (title.length === 0)
      return { isValid: true, suggestedTitle: "" };

    const sanitized = sanitizeInput(title);
    // Handle cases where sanitizeInput returns undefined, null, or empty string
    const safeSanitized = sanitized || "";
    const hasInvalidChars = safeSanitized !== title;

    return {
      isValid: !hasInvalidChars,
      suggestedTitle: safeSanitized.length > 0 ? safeSanitized : (t("album:no-valid-characters-found") ?? "(no valid characters found)"),
    };
  };

  // Helper function to validate performing artist names
  const validatePerformingArtist = (artistName: string) => {
    if (artistName.length === 0)
      return { isValid: true, suggestedName: "" };

    const sanitized = sanitizeInput(artistName);
    // Handle cases where sanitizeInput returns undefined, null, or empty string
    const safeSanitized = sanitized || "";
    const hasInvalidChars = safeSanitized !== artistName;

    return {
      isValid: !hasInvalidChars,
      suggestedName: safeSanitized.length > 0 ? safeSanitized : (t("album:no-valid-characters-found") ?? "(no valid characters found)"),
    };
  };

  // Update form when current song changes
  useEffect(() => {
    if (currentSongData) {
      form.reset({
        songTitle: currentSongData.songTitle,
        durationMins: currentSongData.durationMins,
        durationSecs: currentSongData.durationSecs,
        alternativeTitles: currentSongData.alternativeTitles,
        performingArtists: currentSongData.performingArtists,
        writers: currentSongData.writers,
      });
    }
  }, [currentSongData, form]);

  // Update a specific song's data
  const updateSongData = (songId: string, updates: Partial<SongShareData>) => {
    const updatedShares = {
      ...songShares,
      [songId]: {
        ...songShares[songId],
        ...updates,
      },
    };
    setSongShares(updatedShares);
  };

  // Update a writer's data for the current song
  const updateWriter = (writerIndex: number, updates: Partial<WriterShareData>) => {
    if (currentSong == null || currentSongData == null)
      return;

    const updatedWriters = [...currentSongData.writers];
    updatedWriters[writerIndex] = { ...updatedWriters[writerIndex], ...updates };

    updateSongData(currentSong.songId, { writers: updatedWriters });
  };

  // Remove a writer from the current song
  const removeWriter = (writerIndex: number) => {
    if (currentSong == null || currentSongData == null)
      return;
    if (currentSongData.writers.length <= 1)
      return; // Don't allow removing the last writer

    // Get the writer that will be removed
    const writerToRemove = currentSongData.writers[writerIndex];

    // Update the song shares (local state)
    const updatedWriters = currentSongData.writers.filter((_, index) => index !== writerIndex);
    updateSongData(currentSong.songId, { writers: updatedWriters });

    // Also update the assignment matrix to keep both stores in sync
    // This ensures that when the user goes back to AssignWritersStep, the writer won't be assigned there either
    toggleWriterForSong(currentSong.songId, writerToRemove.writercode);
  };

  // Distribute shares equally among all writers
  const distributeSharesEqually = () => {
    if (currentSong == null || currentSongData == null || currentSongData.writers.length === 0)
      return;

    const numberWriters = currentSongData.writers.length;
    const share = Number((100 / numberWriters).toFixed(2));

    const updatedWriters = currentSongData.writers.map((writer, index) => {
      let writershare: number;
      if (index === numberWriters - 1) {
        // For the last writer, calculate the remaining share to ensure total is exactly 100
        writershare = Number((100 - index * share).toFixed(2));
      }
      else {
        writershare = share;
      }

      return {
        ...writer,
        writershare,
      };
    });

    updateSongData(currentSong.songId, { writers: updatedWriters });
  };

  // Count songs with same writers for apply button (exclude recordings/variants)
  const songsWithSameWriters = React.useMemo(() => {
    if (currentSong == null || currentSongData == null)
      return 0;

    const currentWriterCodes = currentSongData.writers.map(w => w.writercode).sort();

    return songsWithWriters.filter((song) => {
      if (song.songId === currentSong.songId)
        return false;
      // Don't count recordings/variants - they inherit from parent work
      if (song.isRecordingOrVariant)
        return false;
      const songWriterCodes = song.assignedWriters.map(w => w.writercode).sort();
      return JSON.stringify(currentWriterCodes) === JSON.stringify(songWriterCodes);
    }).length;
  }, [currentSong, currentSongData, songsWithWriters]);

  // Check if CAE/IPI is required by site config
  const isCAEIPIRequired = siteConfigData?.ipicae_required === true;

  // Check for writers with missing CAE/IPI numbers when required
  const writersWithoutCAEIPI = React.useMemo(() => {
    if (!isCAEIPIRequired || !currentSongData)
      return [];

    return currentSongData.writers
      .filter(writer => writer.righttocollect === true) // Writers with RTC enabled
      .filter((writer) => {
        const fullWriter = availableWriters.find(w => w.writercode === writer.writercode);
        const caeipinumber = fullWriter?.caeipinumber;
        return (caeipinumber == null) || (caeipinumber.trim() === "");
      })
      .map(writer => writer.writername);
  }, [isCAEIPIRequired, currentSongData, availableWriters]);

  // Apply current song's writer configuration to songs with same writers
  const applyToSongsWithSameWriters = () => {
    // Inline validation check to avoid forward reference issue
    if (currentSong == null || currentSongData == null)
      return;

    if (currentSongData.writers.length === 0)
      return;

    const totalShares = currentSongData.writers.reduce((sum, writer) => sum + writer.writershare, 0);
    const hasRTC = currentSongData.writers.some(writer => writer.righttocollect === true);

    if (!(Math.abs(totalShares - 100) < 0.01 && hasRTC))
      return;

    const currentWriterCodes = currentSongData.writers.map(w => w.writercode).sort();
    let appliedCount = 0;

    // Create a deep copy of the updated song shares to batch update
    const updatedShares = { ...songShares };

    songsWithWriters.forEach((song) => {
      if (song.songId === currentSong.songId)
        return;

      // Skip recordings/variants - they inherit from parent work
      if (song.isRecordingOrVariant)
        return;

      const songWriterCodes = song.assignedWriters.map(w => w.writercode).sort();

      if (JSON.stringify(currentWriterCodes) === JSON.stringify(songWriterCodes)) {
        // Create a deep copy of the current song's writers with all properties
        const writersCopy = currentSongData.writers.map(writer => ({
          writercode: writer.writercode,
          writername: writer.writername,
          writershare: writer.writershare,
          writerdesignationcode: writer.writerdesignationcode,
          righttocollect: writer.righttocollect, // Explicitly copy RTC status
        }));

        // Update the song data in our batch
        updatedShares[song.songId] = {
          ...updatedShares[song.songId],
          writers: writersCopy,
        };
        appliedCount++;
      }
    });

    // Apply all updates at once
    if (appliedCount > 0) {
      setSongShares(updatedShares);
      toast.success(t("album:applied-writer-settings", { count: appliedCount }) ?? `Applied writer settings to ${appliedCount} song${appliedCount > 1 ? "s" : ""} with matching writers`);
    }
  };

  // Handler for writer creation from modal
  const handleWriterCreated = (writer: WriterForSelection) => {
    // Validate that the writer has a valid writercode
    if (!writer.writercode || writer.writercode.trim() === "") {
      toast.error(t("album:invalid-writer-code", { defaultValue: "Invalid writer code. Please try again." }));
      return;
    }

    // Convert WriterForSelection to SelectedWriter format
    const albumWriter = {
      writercode: writer.writercode,
      writername: writer.writername,
      caeipinumber: writer.caeipinumber ?? undefined,
    };

    // Add to selectedWriters store
    addSelectedWriter(albumWriter);

    // Assign the new writer to the current song
    toggleWriterForSong(currentSong.songId, writer.writercode);

    setIsCreateModalOpen(false);

    // IMPORTANT: Wait for writers refetch to complete before showing success
    // This ensures the new writer is in availableWriters before user navigates back
    writersQuery.refetch()
      .then(() => {
        toast.success(t("album:writer-added") ?? "Writer added successfully");
      })
      .catch((error) => {
        // Refetch failed, but writer was still created successfully
        console.error("Failed to refresh writers list:", error);
        toast.success(t("album:writer-added") ?? "Writer added successfully");
      });
  };

  // Handler for when writers are selected from the modal
  const handleWritersSelected = (writers: WriterForSelection[]) => {
    // Add each selected writer to the store and assign to current song
    writers.forEach((writer) => {
      // Convert WriterForSelection to SelectedWriter format
      const albumWriter = {
        writercode: writer.writercode,
        writername: writer.writername,
        caeipinumber: writer.caeipinumber ?? undefined,
      };

      // Add to selectedWriters store
      addSelectedWriter(albumWriter);

      // Assign the writer to the current song
      toggleWriterForSong(currentSong.songId, writer.writercode);
    });

    setIsSelectModalOpen(false);

    // Show success message
    toast.success(t("album:writers-added", { count: writers.length }) ?? `${writers.length} writer(s) added successfully`);
  };

  // Handler for closing select existing writer modal
  const handleSelectModalClose = () => {
    setIsSelectModalOpen(false);
  }; // Calculate total shares for current song
  const totalShares = currentSongData?.writers
    ? currentSongData.writers.reduce((sum, writer) => sum + writer.writershare, 0)
    : 0;

  // Check if current song data is valid
  const isCurrentSongValid = (): boolean => {
    if (!currentSongData || currentSongData.writers.length === 0)
      return false;

    const totalShares = currentSongData.writers.reduce((sum, writer) => sum + writer.writershare, 0);
    const hasRTC = currentSongData.writers.some(writer => writer.righttocollect === true);
    const sharesAndRTCValid = Math.abs(totalShares - 100) < 0.01 && hasRTC;

    // Add Tunecore-specific validation
    if (isTunecore) {
      // Check that all writers with RTC have both DOB and IPI
      const allRTCWritersValid = currentSongData.writers
        .filter(writer => writer.righttocollect === true)
        .every((writer) => {
          const fullWriter = availableWriters.find(w => w.writercode === writer.writercode);
          if (!fullWriter)
            return false;

          const hasDateOfBirth = Boolean(fullWriter.dateofbirth != null && fullWriter.dateofbirth.trim() !== "");
          const hasCAEIPI = Boolean(fullWriter.caeipinumber != null && fullWriter.caeipinumber.trim() !== "");

          return hasDateOfBirth && hasCAEIPI;
        });

      return sharesAndRTCValid && allRTCWritersValid;
    }

    return sharesAndRTCValid;
  };

  // Navigation
  const goToPreviousSong = () => {
    if (currentSongIndex > 0) {
      setCurrentSongIndex(currentSongIndex - 1);
    }
  };

  const goToNextSong = () => {
    if (currentSongIndex < songsWithWriters.length - 1) {
      setCurrentSongIndex(currentSongIndex + 1);
    }
  };

  if (songsWithWriters.length === 0) {
    return (
      <div className="py-8 text-center">
        <Text as="p" className={`text-sm ${isTunecore ? "text-gray-300" : "text-gray-500"}`}>
          {t("album:no-songs-with-writers")}
        </Text>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${isTunecore ? "text-white p-4 rounded-lg bg-[#282828]" : "text-old-sentric-dark-gray"}`}>

      <Heading as="h3" className="inline-flex items-center justify-between w-full mt-2 font-semibold max-md:flex-col" id="albumSongProgressStep">
        <span className="md:w-1/2">
          {currentSong?.songTitle || (t("album:unknown-song") ?? "Unknown Song")}
        </span>
        <span className="max-sm:text-base">
          {currentSongIndex + 1}
          {" / "}
          {songsWithWriters.length}
        </span>
      </Heading>

      {/* Current Song Editing */}
      {currentSongData && (
        <Form {...form}>
          <div className="space-y-6" id="albumSongDetailsStep">
            {/* Song Details Section */}
            <div className="space-y-4">
              <Text as="subtitle">
                {t("album:song-details") ?? "Song Details"}
              </Text>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <FormField
                    control={form.control}
                    name="songTitle"
                    render={({ field }) => {
                      const titleValidation = getTitleValidationInfo(field.value);
                      return (
                        <FormItem>
                          {/* <FormLabel>Song Title</FormLabel> */}
                          <FormLabel>
                            {t("album:song-title") ?? "Song Title"}
                          </FormLabel>
                          <FormControl>
                            <FormInput
                              {...field}
                              // placeholder="Enter song title"
                              placeholder={t("album:enter-song-title") ?? "Enter song title"}
                              className={titleValidation.hasInvalidChars ? `border-red-500 ${isTunecore ? "bg-red-900/20" : "bg-red-50"}` : ""}
                              onChange={(e) => {
                                field.onChange(e);
                                if (currentSong != null) {
                                  updateSongData(currentSong.songId, { songTitle: e.target.value });
                                }
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                          {titleValidation.hasInvalidChars && (
                            <div className={`mt-2 space-y-2 p-3 rounded-md border ${isTunecore ? "bg-[#282828] border-yellow-600" : "bg-yellow-50 border-yellow-200"}`}>
                              <p className={`text-sm mb-2 ${isTunecore ? "text-yellow-400" : "text-yellow-800"}`}>
                                {/* <strong>Warning:</strong> */}
                                <strong>
                                  {t("album:warning") ?? "Warning"}
                                  :
                                </strong>
                                {" "}
                                {t("album:song-title-invalid-characters") ?? "This song title contains invalid characters for our system and can't be registered."}
                                {" "}
                                {t("album:suggested-title") ?? "Suggested title"}
                                : "
                                {titleValidation.suggestedTitle}
                                "
                              </p>
                              {titleValidation.suggestedTitle !== "(no valid characters found)" && (
                                <Button
                                  type="button"
                                  variant="secondary"
                                  onClick={() => {
                                    // Only apply suggested title if it's not our fallback message
                                    if (titleValidation.suggestedTitle !== "(no valid characters found)") {
                                      field.onChange(titleValidation.suggestedTitle);
                                      if (currentSong != null) {
                                        updateSongData(currentSong.songId, { songTitle: titleValidation.suggestedTitle });
                                      }
                                    }
                                  }}
                                  // label="Use Suggested Title"
                                  label={t("album:use-suggested-title") ?? "Use Suggested Title"}
                                />
                              )}
                            </div>
                          )}
                        </FormItem>
                      );
                    }}
                  />
                </div>

                <div className="flex gap-2">
                  <FormField
                    control={form.control}
                    name="durationMins"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormLabel>
                          {t("album:duration-minutes") ?? "Duration (Minutes)"}
                        </FormLabel>
                        <FormControl>
                          <FormNumberInput
                            label=""
                            min={0}
                            max={60}
                            className="w-full"
                            placeholder="0"
                            value={field.value.toString()}
                            onChange={(e) => {
                              const value = Number(e.target.value) || 0;
                              field.onChange(value);
                              if (currentSong != null) {
                                updateSongData(currentSong.songId, { durationMins: value });
                              }
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="durationSecs"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormLabel>
                          {t("album:duration-seconds") ?? "Duration (Seconds)"}
                        </FormLabel>
                        <FormControl>
                          <FormNumberInput
                            label=""
                            min={0}
                            max={59}
                            className="w-full"
                            placeholder="0"
                            value={field.value.toString()}
                            onChange={(e) => {
                              const value = Number(e.target.value) || 0;
                              field.onChange(value);
                              if (currentSong != null) {
                                updateSongData(currentSong.songId, { durationSecs: value });
                              }
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <FormField
                control={form.control}
                name="performingArtists"
                render={({ field }) => {
                  // Check for invalid characters in any performing artist name
                  const invalidArtists = field.value
                    .map((artist, index) => ({ artist, index, ...validatePerformingArtist(artist) }))
                    .filter(item => !item.isValid && item.artist.length > 0);

                  return (
                    <FormItem>
                      <FormLabel>{t("album:performing-artists") ?? "Performing Artists"}</FormLabel>
                      <FormControl>
                        <FormMultipleInput
                          values={field.value}
                          onChange={(values: string[]) => {
                            field.onChange(values);
                            if (currentSong != null) {
                              updateSongData(currentSong.songId, { performingArtists: values });
                            }
                          }}
                          placeholder={t("album:enter-performing-artists") ?? "Enter performing artist names"}
                          addButtonText={t("album:add-performing-artist") ?? "Add Performing Artist"}
                          minItems={0}
                        />
                      </FormControl>
                      <FormMessage />

                      {/* Show validation warnings for invalid performing artist names */}
                      {invalidArtists.length > 0 && (
                        <div className="mt-2 space-y-2">
                          {invalidArtists.map(item => (
                            <div key={item.index} className={`p-3 rounded-md border ${isTunecore ? "bg-[#282828] border-yellow-600" : "bg-yellow-50 border-yellow-200"}`}>
                              <p className={`text-sm mb-2 ${isTunecore ? "text-yellow-400" : "text-yellow-800"}`}>
                                <strong>
                                  {t("album:warning") ?? "Warning"}
                                  :
                                </strong>
                                {" "}
                                {t("album:performing-artist") ?? "Performing artist"}
                                {" "}
                                "
                                {item.artist}
                                "
                                {" "}
                                {t("album:contains-invalid-characters") ?? "contains invalid characters."}
                                {" "}
                                {t("album:suggested-name") ?? "Suggested name"}
                                : "
                                {item.suggestedName}
                                "
                              </p>
                              {item.suggestedName !== "(no valid characters found)" && (
                                <Button
                                  type="button"
                                  variant="secondary"
                                  onClick={() => {
                                    // Only apply suggested name if it's not our fallback message
                                    if (item.suggestedName !== "(no valid characters found)") {
                                      const newValues = [...field.value];
                                      newValues[item.index] = item.suggestedName;
                                      field.onChange(newValues);
                                      if (currentSong != null) {
                                        updateSongData(currentSong.songId, { performingArtists: newValues });
                                      }
                                    }
                                  }}
                                  label={t("album:use-suggested-name") ?? "Use Suggested Name"}
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </FormItem>
                  );
                }}
              />

              <FormField
                control={form.control}
                name="alternativeTitles"
                render={({ field }) => {
                  // Check for invalid characters in any alternative title
                  const invalidTitles = field.value
                    .map((title, index) => ({ title, index, ...validateAlternativeTitle(title) }))
                    .filter(item => !item.isValid && item.title.length > 0);

                  return (
                    <FormItem>
                      <FormLabel>
                        {t("album:alternative-titles") ?? "Alternative Titles"}
                      </FormLabel>
                      <FormControl>
                        <FormMultipleInput
                          values={field.value}
                          onChange={(values: string[]) => {
                            field.onChange(values);
                            if (currentSong != null) {
                              updateSongData(currentSong.songId, { alternativeTitles: values });
                            }
                          }}
                          // placeholder="Enter alternative titles"
                          placeholder={t("album:enter-alternative-titles") ?? "Enter alternative titles"}
                          // addButtonText="Add Alternative Title"
                          addButtonText={t("album:add-alternative-title") ?? "Add Alternative Title"}
                          minItems={0}
                        />
                      </FormControl>
                      <FormMessage />

                      {/* Show validation warnings for invalid alternative titles */}
                      {invalidTitles.length > 0 && (
                        <div className="mt-2 space-y-2">
                          {invalidTitles.map(item => (
                            <div key={item.index} className={`p-3 rounded-md border ${isTunecore ? "bg-[#282828] border-yellow-600" : "bg-yellow-50 border-yellow-200"}`}>
                              <p className={`text-sm mb-2 ${isTunecore ? "text-yellow-400" : "text-yellow-800"}`}>
                                <strong>
                                  {t("album:warning") ?? "Warning"}
                                  :
                                </strong>
                                {" "}
                                {t("album:alternative-title") ?? "Alternative title"}
                                {" "}
                                "
                                {item.title}
                                "
                                {" "}
                                {t("album:contains-invalid-characters") ?? "contains invalid characters."}
                                {" "}
                                {t("album:suggested-title") ?? "Suggested title"}
                                : "
                                {item.suggestedTitle}
                                "
                              </p>
                              {item.suggestedTitle !== "(no valid characters found)" && (
                                <Button
                                  type="button"
                                  variant="secondary"
                                  onClick={() => {
                                    // Only apply suggested title if it's not our fallback message
                                    if (item.suggestedTitle !== "(no valid characters found)") {
                                      const newValues = [...field.value];
                                      newValues[item.index] = item.suggestedTitle;
                                      field.onChange(newValues);
                                      if (currentSong != null) {
                                        updateSongData(currentSong.songId, { alternativeTitles: newValues });
                                      }
                                    }
                                  }}
                                  // label="Use Suggested Title"
                                  label={t("album:use-suggested-title") ?? "Use Suggested Title"}
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </FormItem>
                  );
                }}
              />
            </div>
          </div>
        </Form>
      )}

      {/* Writer Shares Section */}
      {currentSongData && (
        <div className="space-y-4">
          {/* Warning for recording shares */}
          {isRecording && (() => {
            const isIntraAlbumMatch = typeof currentTrack?.suspectedAlbumTrackId === "string" && currentTrack.suspectedAlbumTrackId.length > 0;
            const targetTitle = isIntraAlbumMatch
              ? currentTrack?.suspectedAlbumTrackTitle
              : currentTrack?.suspectedWorkTitle;
            const workType = isIntraAlbumMatch ? (t("album:track-in-this-album") ?? "track in this album") : (t("album:existing-work") ?? "existing work");

            return (
              <div className="space-y-3">
                <ErrorContainer
                  variant="warning"
                  title={t("album:recording-added-to-existing-work") ?? "Recording Being Added to Existing Work"}
                  message={t("album:recording-inherits-shares", {
                    workType,
                    targetTitle: (typeof targetTitle === "string" && targetTitle.length > 0) ? targetTitle : (t("album:unknown") ?? "Unknown"),
                  }) ?? `This track is being added as a recording to the ${workType} "${(typeof targetTitle === "string" && targetTitle.length > 0) ? targetTitle : "Unknown"}". Recordings inherit publishing shares from the original work.`}
                />
                <div className="flex items-center gap-3 ">
                  <Text as="p">
                    {t("album:unlock-to-submit-separate-work") ?? "Want to submit this as a separate work instead? Click \"Unlock\" to enable writer shares and convert this to a new song submission."}
                  </Text>
                  <Button
                    variant="secondary"
                    label={t("common:unlock") ?? "Unlock"}
                    onClick={() => setIsUnlockConfirmOpen(true)}
                  />
                </div>

                {/* Unlock Confirmation Modal */}
                <Dialog open={isUnlockConfirmOpen} onOpenChange={setIsUnlockConfirmOpen}>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle>
                        {t("album:convert-to-new-song") ?? "Convert to New Song?"}
                      </DialogTitle>
                      <DialogDescription>
                        {t("album:convert-to-new-song-warning") ?? "This will convert the track into a new song registration. You will need to assign writers and shares manually. This action cannot be undone without restarting the album import."}
                      </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="gap-2 mt-4 sm:gap-0">
                      <Button
                        variant="secondary"
                        label={t("common:cancel") ?? "Cancel"}
                        onClick={() => setIsUnlockConfirmOpen(false)}
                      />
                      <Button
                        variant="primary"
                        label={t("album:yes-convert") ?? "Yes, Convert"}
                        onClick={() => {
                          // currentTrack is guaranteed non-null here because isRecording depends on it
                          setTrackReviewAction(currentTrack.id, "dismiss");
                          setIsUnlockConfirmOpen(false);
                          toast.success(t("album:track-unlocked") ?? "Track unlocked - you can now add writers and shares");
                        }}
                      />
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            );
          })()}

          <div id="albumWriterSharesStep" className={`relative ${isRecording ? "pointer-events-none select-none" : ""}`}>
            {isRecording && (
              <div className={`absolute inset-0 z-10 rounded-lg backdrop-blur-sm  ${isTunecore ? "" : "bg-white/50"}`} />
            )}
            <div className="flex flex-col items-center justify-between max-md:gap-4 md:flex-row">
              <div className="flex flex-col items-start text-start">
                <Text as="subtitle">
                  {t("album:writer-shares-roles") ?? "Writer Shares & Roles"}
                </Text>
                <div className="flex flex-col">
                  <Text
                    as="p"
                    className={`font-medium ${Math.abs(totalShares - 100) <= 0.01 ? "text-green-600" : "text-red-600"
                    }`}
                  >
                    {t("album:total-shares") ?? "Total Shares"}
                    :
                    {" "}
                    {totalShares.toFixed(2)}
                    %
                  </Text>
                  {!currentSongData.writers.some(w => w.righttocollect) && (
                    <Text as="p" className={`text-sm ${isTunecore ? "text-red-400" : "text-red-600"}`}>
                      {t("album:at-least-one-writer-rtc") ?? "At least one writer must have 'Right To Collect' enabled."}
                    </Text>
                  )}
                  {writersWithoutCAEIPI.length > 0 && (
                    <Text
                      as="p"
                      className={`text-sm ${isCAEIPIRequired
                        ? (isTunecore ? "text-red-400" : "text-red-600")
                        : (isTunecore ? "text-yellow-400" : "text-orange-600")
                      }`}
                    >
                      {isCAEIPIRequired ? (
                        <>
                          <strong>
                            {t("album:error") ?? "Error"}
                            :
                          </strong>
                          {" "}
                          {t("songs:writers-rtc-must-have-cae-ipi") ?? "Writers with 'Right To Collect' must have CAE/IPI numbers"}
                          {": "}
                          {writersWithoutCAEIPI.join(", ")}
                        </>
                      ) : (
                        <>
                          <strong>
                            {t("album:note") ?? "Note"}
                            :
                          </strong>
                          {" "}
                          {t("album:writers-missing-cae-ipi", "Writers missing CAE/IPI numbers")}
                          {": "}
                          {writersWithoutCAEIPI.join(", ")}
                        </>
                      )}
                    </Text>
                  )}
                </div>
              </div>

              <div className="flex flex-col items-center gap-4 md:flex-row">
                <Button
                  type="button"
                  className="w-max"
                  onClick={distributeSharesEqually}
                  variant="secondary"
                  disabled={currentSongData.writers.length === 0}
                  label={t("album:distribute-equally") ?? "Distribute Equally"}
                />
                {songsWithSameWriters > 0 && (
                  <Button
                    type="button"
                    onClick={applyToSongsWithSameWriters}
                    variant="primary"
                    disabled={!isCurrentSongValid()}
                    label={t("album:apply-to-other-songs", {
                      count: songsWithSameWriters,
                    }) ?? `Apply to ${songsWithSameWriters} other song${songsWithSameWriters > 1 ? "s" : ""} with same writers`}
                    icon={<IconTick className={isTunecore ? "stroke-white stroke-[3]" : "stroke-black stroke-[3]"} />}
                  />
                )}
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="link"
                    onClick={() => setIsSelectModalOpen(true)}
                    icon={<IconUser className={`w-5 h-5 ${isTunecore ? "stroke-white group-hover:stroke-tunecore-green" : "stroke-black group-hover:stroke-sentric-orange"}`} />}
                    tooltip
                    tooltipText={t("writers:select-existing-writer") ?? "Select Existing Writer"}
                    className="group"
                  />
                  <Button
                    type="button"
                    variant="link"
                    onClick={() => setIsCreateModalOpen(true)}
                    icon={<IconCreate className={`w-5 h-5 ${isTunecore ? "stroke-white group-hover:stroke-tunecore-green" : "stroke-black group-hover:stroke-sentric-orange"}`} />}
                    tooltip
                    tooltipText={t("album:create-new-writer") ?? "Create New Writer"}
                    className="group"
                  />
                </div>
              </div>
            </div>

            {/* Writers List */}
            <div className="space-y-3">
              {currentSongData.writers.map((writer, index) => {
                // Find the full writer info to get CAE/IPI number
                const fullWriter = availableWriters.find(w => w.writercode === writer.writercode);
                const ipiNumber = fullWriter?.caeipinumber;
                const hasIMRORegistrationPending = Boolean(fullWriter?.writerexternalregistrationstatus != null);

                return (
                  <div key={writer.writercode} className={`flex flex-col md:flex-row items-center gap-4 p-4 rounded-lg shadow-sm transition-colors ${isTunecore ? "bg-tunecore-grey-element" : "bg-white border border-sentric-mid-grey"}`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Text as="p" className="font-semibold">
                          {writer.writername}
                        </Text>
                      </div>
                      <Text as="p" className={isTunecore ? "text-gray-300" : "text-gray-600"}>
                        {ipiNumber != null && ipiNumber.trim() !== "" ? (
                          <>
                            {t("songs:cae-ipi") ?? "CAE/IPI"}
                            :
                            {" "}
                            {ipiNumber}
                          </>
                        ) : (
                          <>
                            {t("songs:no-cae-ipi") ?? "No CAE/IPI"}
                            {(isCAEIPIRequired || isTunecore) && (
                              <span className={`block text-xs ${isTunecore ? "text-yellow-400" : "text-red-500"}`}>
                                {isTunecore
                                  ? (hasIMRORegistrationPending
                                      ? (t("songs:imro-writers-only-need-dob-for-rtc") ?? "Writers with pending IMRO registration only need Date of Birth to use Right To Collect")
                                      : (t("songs:tunecore-writers-need-dob-ipi-for-rtc") ?? "For Tunecore, writers need both Date of Birth and CAE/IPI to use Right To Collect"))
                                  : (t("songs:not-eligible-for-rtc") ?? "Writers with no CAE/IPI number are not eligible for Right To Collect")}
                              </span>
                            )}
                          </>
                        )}
                      </Text>
                      {/* Show DOB info for Tunecore */}
                      {isTunecore && (
                        <Text as="p" className="text-gray-300">
                          {fullWriter?.dateofbirth != null && fullWriter.dateofbirth.trim() !== "" ? (
                            <>
                              {t("writers:date-of-birth") ?? "Date of Birth"}
                              :
                              {" "}
                              {(() => {
                                try {
                                  const date = new Date(fullWriter.dateofbirth);
                                  return date.toLocaleDateString("en-GB", {
                                    year: "numeric",
                                    month: "2-digit",
                                    day: "2-digit",
                                  });
                                }
                                catch {
                                  return fullWriter.dateofbirth;
                                }
                              })()}
                            </>
                          ) : (
                            <>
                              {t("songs:no-date-of-birth") ?? "No Date of Birth"}
                              <span className="block text-xs text-yellow-400">
                                {hasIMRORegistrationPending
                                  ? (t("songs:imro-writers-only-need-dob-for-rtc") ?? "Writers with pending IMRO registration only need Date of Birth to use Right To Collect")
                                  : (t("songs:tunecore-writers-need-dob-ipi-for-rtc") ?? "For Tunecore, writers need both Date of Birth and CAE/IPI to use Right To Collect")}
                              </span>
                            </>
                          )}
                        </Text>
                      )}
                    </div>
                    <div className="flex flex-col items-center gap-5 md:flex-row">
                      <Select
                        value={writer.writerdesignationcode.toString()}
                        onValueChange={value => updateWriter(index, {
                          writerdesignationcode: Number.parseInt(value, 10),
                        })}
                        disabled={isSharesLocked}
                      >
                        <SelectTrigger className="w-44">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {DESIGNATION_OPTIONS.map(option => (
                            <SelectItem key={option.value} value={option.value.toString()}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      <FormNumberInput
                        type="number"
                        value={writer.writershare.toString()}
                        onChange={e => updateWriter(index, { writershare: Number.parseFloat(e.target.value) || 0 })}
                        placeholder={t("album:share-percentage") ?? "Share %"}
                        className="md:w-20"
                        min={0}
                        max={100}
                        showPercentage={true}
                        step={0.01}
                        disabled={isSharesLocked}
                      />

                      <FormCheckbox
                        label={t("album:right-to-collect") ?? "Right To Collect"}
                        checked={writer.righttocollect}
                        onChange={e => updateWriter(index, { righttocollect: e.target.checked })}
                        disabled={
                          isSharesLocked
                          || (isCAEIPIRequired && (ipiNumber == null || ipiNumber.trim() === ""))
                          || (isTunecore && (
                            (ipiNumber == null || ipiNumber.trim() === "")
                            || (fullWriter?.dateofbirth == null || fullWriter.dateofbirth.trim() === "")
                          ))
                        }
                      />

                      <Button
                        type="button"
                        onClick={() => removeWriter(index)}
                        variant="link"
                        className="text-red-600 hover:text-red-700"
                        disabled={currentSongData.writers.length <= 1}
                        icon={<IconBin className="w-5 h-5" />}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Share Total Display */}
          <div>

            <div className="flex flex-col justify-center gap-6 p-4 rounded-lg md:flex-row md:items-center" id="albumSongNavStep">

              <Button
                onClick={goToPreviousSong}
                disabled={currentSongIndex === 0}
                variant="secondary"
                className="min-w-32"
                label={(
                  <div className="flex items-center gap-2">
                    <IconChevronLeft className={isTunecore ? "stroke-white" : "stroke-black"} />
                    {t("album:previous-song") ?? "Previous"}
                  </div>
                )}
              />

              <Button
                onClick={goToNextSong}
                disabled={currentSongIndex === songsWithWriters.length - 1}
                variant="secondary"
                className="min-w-32"
                label={(
                  <div className="flex items-center gap-2">
                    {t("album:next-song") ?? "Next"}
                    <IconChevronRight className={isTunecore ? "stroke-white" : "stroke-black"} />
                  </div>
                )}
              />

            </div>

          </div>
        </div>
      )}

      {/* Select Existing Writer Modal */}
      <Dialog open={isSelectModalOpen} onOpenChange={setIsSelectModalOpen}>
        <DialogContent className="max-w-4xl">
          <SelectExistingWritersModal
            availableWriters={availableWriters}
            selectedWriters={selectedWriters}
            onWritersSelected={handleWritersSelected}
            onClose={handleSelectModalClose}
          />
        </DialogContent>
      </Dialog>

      {/* Create New Writer Modal */}
      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent className="max-w-2xl">
          <CreateWriterModal
            onWriterCreated={handleWriterCreated}
            onClose={() => setIsCreateModalOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
